/*
 * Chez Perfumer — motion & enhancements.
 * - Scroll reveals for `.chez-reveal` and word-by-word heading reveals for `[data-chez-split]`.
 * - Scroll-scrubbed statement text for `[data-chez-scrub]`.
 * - Scroll progress (`--p`, 0 → 1) for `[data-chez-progress]` sections, used for parallax.
 * - Cursor ring with contextual labels (`[data-cursor]`) and magnetic buttons, fine pointers only.
 * - Count-up for `[data-chez-count]` prices.
 * - Header "scrolled" state and newsletter validation.
 * Nothing is hidden without JavaScript, in the theme editor, or with reduced motion.
 */
(() => {
  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const motionAllowed = () => !reducedMotion.matches && !window.Shopify?.designMode;

  /* ---------- Word splitting ---------- */

  function splitWords(element) {
    if (element.dataset.chezSplitDone) return [];
    element.dataset.chezSplitDone = 'true';
    // Split words are inline-blocks, which bidi treats as neutral: untranslated Latin text
    // on an RTL page would come out in reverse word order, so pin its direction.
    if (!element.hasAttribute('dir') && getComputedStyle(element).direction === 'rtl' && !/\p{Script=Arabic}|\p{Script=Hebrew}/u.test(element.textContent)) {
      element.setAttribute('dir', 'ltr');
    }
    const words = [];
    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const parts = child.textContent.split(/(\s+)/);
          const fragment = document.createDocumentFragment();
          parts.forEach((part) => {
            if (part === '') return;
            if (/^\s+$/.test(part)) {
              fragment.appendChild(document.createTextNode(part));
              return;
            }
            const outer = document.createElement('span');
            outer.className = 'chez-w';
            const inner = document.createElement('span');
            inner.className = 'chez-w__i';
            inner.textContent = part;
            outer.appendChild(inner);
            fragment.appendChild(outer);
            words.push(outer);
          });
          child.replaceWith(fragment);
        } else if (child.nodeType === Node.ELEMENT_NODE && child.tagName !== 'BR') {
          walk(child);
        }
      });
    };
    walk(element);
    words.forEach((word, index) => word.style.setProperty('--wi', index));
    return words;
  }

  /* ---------- Reveals ---------- */

  let revealObserver;

  function initReveals(scope = document) {
    const reveals = Array.from(scope.querySelectorAll('.chez-reveal:not(.is-visible)'));
    const splits = Array.from(scope.querySelectorAll('[data-chez-split]:not(.is-in)'));

    if (!motionAllowed() || !('IntersectionObserver' in window)) {
      reveals.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    splits.forEach((element) => splitWords(element));
    root.classList.add('chez-motion');

    // Anything already on screen is shown immediately (hero headings still animate on load).
    const viewportHeight = window.innerHeight;
    reveals.forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (rect.top < viewportHeight * 0.92 && rect.bottom > 0) element.classList.add('is-visible');
    });
    root.classList.add('chez-reveal-ready');

    requestAnimationFrame(() => {
      splits.filter((element) => element.dataset.chezSplit === 'load').forEach((element) => element.classList.add('is-in'));
    });

    revealObserver ||= new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add(entry.target.hasAttribute('data-chez-split') ? 'is-in' : 'is-visible');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
    );
    reveals.filter((element) => !element.classList.contains('is-visible')).forEach((element) => revealObserver.observe(element));
    splits.filter((element) => element.dataset.chezSplit !== 'load').forEach((element) => revealObserver.observe(element));
  }

  /* ---------- Scroll-linked effects (one rAF loop for all) ---------- */

  const scrubs = [];
  const progresses = [];

  function initScrollEffects() {
    if (!motionAllowed()) return;

    document.querySelectorAll('[data-chez-scrub]').forEach((element) => {
      const words = splitWords(element);
      element.classList.add('is-scrubbing');
      scrubs.push({ element, words, lit: -1 });
    });
    document.querySelectorAll('[data-chez-progress]').forEach((element) => progresses.push(element));
    if (!scrubs.length && !progresses.length) return;

    let ticking = false;
    const update = () => {
      ticking = false;
      const vh = window.innerHeight;

      scrubs.forEach((scrub) => {
        const rect = scrub.element.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return;
        // 0 when the block's top reaches 85% of the viewport, 1 when its bottom reaches 40%.
        const start = vh * 0.85;
        const end = vh * 0.4;
        const progress = Math.min(1, Math.max(0, (start - rect.top) / (start - end + rect.height)));
        const lit = Math.round(progress * scrub.words.length);
        if (lit === scrub.lit) return;
        scrub.words.forEach((word, index) => word.classList.toggle('is-lit', index < lit));
        scrub.lit = lit;
      });

      progresses.forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return;
        // "through": 0 entering at the bottom of the viewport → 1 leaving at the top.
        // default: 0 when the section's top meets the viewport top → 1 when it has scrolled away.
        const raw =
          element.dataset.chezProgress === 'through'
            ? (vh - rect.top) / (vh + rect.height)
            : -rect.top / Math.max(rect.height, 1);
        const p = Math.min(1, Math.max(0, raw));
        element.style.setProperty('--p', p.toFixed(4));
      });
    };

    const request = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request, { passive: true });
    update();
  }

  /* ---------- Cursor ring & magnetic buttons ---------- */

  function initCursor() {
    if (!finePointer.matches || !motionAllowed()) return;

    const viewLabel = document.body.dataset.cursorView;
    if (viewLabel) {
      document.querySelectorAll('.card-wrapper .card__inner').forEach((card) => {
        if (!card.dataset.cursor) card.dataset.cursor = viewLabel;
      });
    }

    const cursor = document.createElement('div');
    cursor.className = 'chez-cursor';
    cursor.setAttribute('aria-hidden', 'true');
    cursor.innerHTML = '<span class="chez-cursor__label"></span>';
    document.body.appendChild(cursor);
    const label = cursor.firstElementChild;

    let x = -100;
    let y = -100;
    let cx = x;
    let cy = y;
    let frame = null;

    const loop = () => {
      cx += (x - cx) * 0.2;
      cy += (y - cy) * 0.2;
      cursor.style.transform = `translate3d(${cx}px, ${cy}px, 0)`;
      frame = Math.abs(x - cx) + Math.abs(y - cy) > 0.1 ? requestAnimationFrame(loop) : null;
    };

    document.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerType !== 'mouse') return;
        x = event.clientX;
        y = event.clientY;
        cursor.classList.add('is-active');
        if (!frame) frame = requestAnimationFrame(loop);

        const labelled = event.target.closest?.('[data-cursor]');
        const interactive = event.target.closest?.('a, button, label, summary, input, select, textarea, [role="button"]');
        const text = labelled && !event.target.closest('input, textarea, select') ? labelled.dataset.cursor : '';
        if (label.textContent !== text) label.textContent = text;
        cursor.classList.toggle('has-label', Boolean(text));
        cursor.classList.toggle('is-hovering', Boolean(interactive) && !text);
      },
      { passive: true }
    );
    document.addEventListener('pointerleave', () => cursor.classList.remove('is-active'));
    document.addEventListener('pointerdown', () => cursor.classList.add('is-pressed'));
    document.addEventListener('pointerup', () => cursor.classList.remove('is-pressed'));
  }

  function initMagnetic() {
    if (!finePointer.matches || !motionAllowed()) return;
    document.querySelectorAll('.chez-magnetic, .chez-icon-button').forEach((element) => {
      element.addEventListener('pointermove', (event) => {
        const rect = element.getBoundingClientRect();
        const dx = event.clientX - (rect.left + rect.width / 2);
        const dy = event.clientY - (rect.top + rect.height / 2);
        element.style.setProperty('--mx', `${(dx * 0.22).toFixed(1)}px`);
        element.style.setProperty('--my', `${(dy * 0.3).toFixed(1)}px`);
      });
      element.addEventListener('pointerleave', () => {
        element.style.removeProperty('--mx');
        element.style.removeProperty('--my');
      });
    });
  }

  /* ---------- Deal-out gallery ---------- */

  // Tiles start stacked at the centre of their grid, like a deck of photos, and are
  // dealt into place as the section scrolls in (CSS reads --sx/--sy and the
  // section's --p). Offsets are measured from the laid-out grid.
  function initDeal() {
    const grids = document.querySelectorAll('[data-chez-deal]');
    if (!grids.length || !motionAllowed()) return;
    const measure = () => {
      grids.forEach((grid) => {
        grid.classList.remove('is-dealing'); // measure the untransformed layout
        const box = grid.getBoundingClientRect();
        const cx = box.left + box.width / 2;
        const cy = box.top + box.height / 2;
        Array.from(grid.children).forEach((tile, i) => {
          const rect = tile.getBoundingClientRect();
          tile.style.setProperty('--sx', `${(cx - (rect.left + rect.width / 2)).toFixed(1)}px`);
          tile.style.setProperty('--sy', `${(cy - (rect.top + rect.height / 2)).toFixed(1)}px`);
          tile.style.setProperty('--sr', `${[-7, 5, -3, 8, -5, 4][i % 6]}deg`);
        });
        grid.classList.add('is-dealing');
      });
    };
    measure();
    let timer;
    window.addEventListener('resize', () => {
      clearTimeout(timer);
      timer = setTimeout(measure, 150);
    });
  }

  /* ---------- Count-up ---------- */

  function initCountUp() {
    const elements = document.querySelectorAll('[data-chez-count]');
    if (!elements.length || !motionAllowed() || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          const element = entry.target;
          const original = element.textContent;
          const match = original.match(/[\d.,]+\d/);
          if (!match) return;
          const target = parseFloat(match[0].replace(/,/g, ''));
          const decimals = (match[0].split('.')[1] || '').length;
          const started = performance.now();
          const duration = 1400;
          const tick = (now) => {
            const t = Math.min(1, (now - started) / duration);
            const eased = 1 - Math.pow(1 - t, 4);
            const value = (target * eased).toLocaleString('en-US', {
              minimumFractionDigits: decimals,
              maximumFractionDigits: decimals,
            });
            element.textContent = original.replace(match[0], value);
            if (t < 1) requestAnimationFrame(tick);
            else element.textContent = original;
          };
          requestAnimationFrame(tick);
        });
      },
      { threshold: 0.6 }
    );
    elements.forEach((element) => observer.observe(element));
  }

  /* ---------- In-page links: animated scroll + arrival ---------- */

  // Links to a section on the same page (e.g. "/#find-your-set") glide there with an
  // eased scroll instead of jumping, then the section plays a short arrival animation.
  function arrive(target) {
    const section = target.closest('section') || target;
    section.querySelectorAll('.chez-match').forEach((item, i) => item.style.setProperty('--mi', i));
    section.classList.remove('chez-arrive');
    void section.offsetWidth; // restart the animation on repeat clicks
    section.classList.add('chez-arrive');
    setTimeout(() => section.classList.remove('chez-arrive'), 2200);
  }

  function initAnchorScroll() {
    document.addEventListener('click', (event) => {
      const link = event.target.closest?.('a[href*="#"]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
      const url = new URL(link.href, location.href);
      if (url.pathname !== location.pathname || url.origin !== location.origin || url.hash.length < 2) return;
      const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
      if (!target) return;
      event.preventDefault();

      const focusTarget = () => {
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
        arrive(target);
      };
      history.pushState(null, '', url.hash);

      const start = window.scrollY;
      const end = start + target.getBoundingClientRect().top;
      const distance = end - start;
      if (!motionAllowed() || Math.abs(distance) < 4) {
        window.scrollTo(0, end);
        focusTarget();
        return;
      }

      // ease-in-out over a duration that grows gently with distance
      const duration = Math.min(1800, 700 + Math.abs(distance) * 0.12);
      const started = performance.now();
      const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      let cancelled = false;
      const cancel = () => {
        cancelled = true;
      };
      window.addEventListener('wheel', cancel, { once: true, passive: true });
      window.addEventListener('touchstart', cancel, { once: true, passive: true });
      const step = (now) => {
        if (cancelled) return;
        const t = Math.min(1, (now - started) / duration);
        window.scrollTo(0, start + distance * ease(t));
        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          window.removeEventListener('wheel', cancel);
          window.removeEventListener('touchstart', cancel);
          focusTarget();
        }
      };
      requestAnimationFrame(step);
    });

    // Arriving from another page with a hash, e.g. /#find-your-set
    if (location.hash.length > 1 && motionAllowed()) {
      const target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
      if (target) window.addEventListener('load', () => arrive(target), { once: true });
    }
  }

  /* ---------- Header & newsletter ---------- */

  function initHeaderState() {
    const header = document.querySelector('.section-header');
    if (!header) return;
    let ticking = false;
    const update = () => {
      header.classList.toggle('chez-scrolled', window.scrollY > 8);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
  }

  function initNewsletter() {
    document.querySelectorAll('.chez-newsletter__form').forEach((form) => {
      const input = form.querySelector('input[type="email"]');
      const message = form.querySelector('.chez-field__message');
      const field = form.querySelector('.chez-field');
      if (!input || !message) return;
      const defaultText = message.textContent;

      const setError = (hasError) => {
        field.classList.toggle('chez-field--error', hasError);
        if (hasError) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
        message.textContent = hasError ? message.dataset.invalidMessage : defaultText;
      };

      form.addEventListener('submit', (event) => {
        if (input.checkValidity() && input.value.trim() !== '') return;
        event.preventDefault();
        setError(true);
        input.focus();
      });
      input.addEventListener('input', () => {
        if (field.classList.contains('chez-field--error') && input.checkValidity()) setError(false);
      });
    });
  }

  function init() {
    initReveals();
    initScrollEffects();
    initCursor();
    initMagnetic();
    initDeal();
    initCountUp();
    initAnchorScroll();
    initHeaderState();
    initNewsletter();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Sections re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', (event) => initReveals(event.target));
})();
